'use client';
import {
  FC,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { toast } from 'react-toastify';

import { endpoints, http, storage } from '@/config';
import { getKeyValue, pushGA4Event } from '@/utils/helper';
import { useCleverTap } from './clevertapContext';
import { AddressExtensionAttributes } from '@/models/user-details';
import { deleteCookie, getCookie } from 'cookies-next';

interface Address {
  id: number;
  customer_id: number;
  region: {
    region_code: string;
    region: string;
    region_id: number;
  };
  region_id: number;
  country_id: string;
  street: string[];
  telephone: string;
  // postcode: string;
  city: string;
  firstname: string;
  lastname: string;
  default_shipping: boolean;
  extension_attributes: AddressExtensionAttributes;
}

interface UserPropTypes {
  firstname: string;
  lastname: string;
  email: string;
  mobilenumber: string;
  id: number;
  token: string;
  store_id: number;
  group_id: number;
  website_id: string;
  isSubscribed: boolean;
  addresses: Address[];
  dob?: string;
  wedding_anniversary?: string;
}

interface PropTypes {
  children: React.ReactNode;
}

const Context = createContext<any | null>(null);

const UserProvider: FC<PropTypes> = ({ children }) => {
  const { clevertapUserLogin } = useCleverTap();

  const [loading, setLoading] = useState<boolean>(false);
  const [userDetails, setUserDetails] = useState<UserPropTypes | null>(null);

  // Login modal states
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [loginRedirect, setLoginRedirect] = useState<string>('');
  const [loginModalMessage, setLoginModalMessage] = useState<string>('');
  const [loginEventMetadata, setLoginEventMetadata] = useState<
    | {
        source?: string;
      }
    | undefined
  >();
  const [asGuest, setAsGuest] = useState<boolean>(false);

  const reset = useCallback(async () => {
    setUserDetails(null);
    storage.remove('token');
    storage.remove('quote_id');
    deleteCookie('token');
    deleteCookie('quote_id');
    window.location.href = '/';
  }, []);

  const onLogout = useCallback(async () => {
    await reset();
    toast.success('Logged out successfully');
  }, [reset]);

  const verifyToken = useCallback(async () => {
    setLoading(true);
    try {
      const { response, data } = await http.get(endpoints.PROFILE_ENDPOINT);
      if (response.ok) {
        const customDetails = getKeyValue(
          data?.custom_attributes,
          'attribute_code'
        );
        const updatedUser = {
          ...data,
          mobilenumber: customDetails.mobile_number,
          wedding_anniversary:
            customDetails?.wedding_anniversary?.split(' ')[0],
          token: storage.get('token'),
        };
        setUserDetails(updatedUser);
      } else throw new Error();
    } catch {
      reset();
    }
    setLoading(false);
  }, [reset]);

  const updateUserDetails = useCallback(
    async (details: any) => {
      const { response, data } = await http.get(endpoints.PROFILE_ENDPOINT);
      if (response.ok) {
        const customDetails = getKeyValue(
          data?.custom_attributes,
          'attribute_code'
        );
        setUserDetails(prev => ({
          ...prev,
          ...data,
          ...details,
          mobilenumber: customDetails.mobile_number,
          wedding_anniversary:
            customDetails?.wedding_anniversary?.split(' ')[0],
          token: prev?.token ?? storage.get('token'),
        }));
        clevertapUserLogin({
          ...data,
          ...details,
          mobilenumber: customDetails.mobile_number,
        });
      }
    },
    [clevertapUserLogin]
  );

  const verifyWizzyId = useCallback(async () => {
    const wizzy_sessionId = await getCookie('wizzy_sessionId');
    const wizzy_userId = await getCookie('wizzy_userId');
    if (wizzy_sessionId) {
      sessionStorage.setItem('wizzy_sessionId', wizzy_sessionId);
    }
    if (wizzy_userId) {
      storage.set('wizzy_userId', wizzy_userId);
    }
  }, []);

  const verifyUserId = useCallback(async () => {
    const userId = await getCookie('user-id');
    if (userId) {
      storage.set('user-id', userId);

      // GA4 User Identity (session) - minimal, direct events
      try {
        // GA4 Acquisition: first_visit (once per browser)
        const firstVisitDone = localStorage.getItem('ga4_first_visit_done');
        if (!firstVisitDone) {
          pushGA4Event('first_visit', { user_id: String(userId) });
          localStorage.setItem('ga4_first_visit_done', '1');
        }

        const firstSessionDone = localStorage.getItem('ga4_first_session_done');
        const sessionSent = sessionStorage.getItem(
          'ga4_user_identity_session_sent'
        );
        if (!sessionSent) {
          pushGA4Event(
            firstSessionDone ? 'returning_user_session' : 'first_session',
            {
              user_id: String(userId),
            }
          );
          sessionStorage.setItem('ga4_user_identity_session_sent', '1');
          localStorage.setItem('ga4_first_session_done', '1');
        }

        const hasPurchased = localStorage.getItem('ga4_has_purchased') === '1';
        pushGA4Event('user_type_identified', {
          user_id: String(userId),
          user_type: hasPurchased ? 'repeat' : 'new',
        });
      } catch {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    verifyUserId();
  }, [verifyUserId]);

  useEffect(() => {
    const token = storage.get('token');
    if (token) {
      verifyToken();
    }
  }, [verifyToken]);

  useEffect(() => {
    verifyWizzyId();
  }, [verifyWizzyId]);

  const value: any = {
    userDetails,
    loading,
    showLoginModal,
    loginRedirect,
    loginModalMessage,
    asGuest,
    loginEventMetadata,
    onLogout,
    setUserDetails,
    updateUserDetails,
    setShowLoginModal,
    setLoginRedirect,
    setLoginModalMessage,
    setAsGuest,
    setLoginEventMetadata,
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useUser = () => useContext(Context);

export default UserProvider;
